import { create } from 'zustand'

/**
 * "Decision in flight" markers for AI suggestion cards — the only optimistic state a decision has.
 *
 * A decision is a queued PATCH, so its answer is a mock 202 that says nothing about the outcome:
 * the server may apply, partially apply or refuse it. The card therefore never writes the suggested
 * values into the issue cache; it shows the marker until the decided suggestion arrives (Mercure
 * or GET, see issueAiSuggestions.ts), or until the queue reports a permanent failure, which turns
 * the marker into a refusal the card shows next to its choices again.
 *
 * Keyed by suggestion id: one decision per suggestion at a time. Memory only, but the queue row of
 * the PATCH is not: after a restart `useAiSuggestionDecisionMarker` derives the marker from that row
 * and restores it here, so a decision still queued keeps its card locked, and a refused one keeps
 * its Remove, plus Retry unless the refusal was a 409 (a reused decisionRequestUUID, which a replay
 * would only repeat).
 */

export type AiSuggestionDecisionKindT = 'apply' | 'reject'

export type AiSuggestionDecisionFailureT = {
  /** HTTP status of the refusal; null when the request never reached the queue or the server. */
  status: number | null
  detail: string | null
}

export type AiSuggestionDecisionMarkerT = {
  suggestionId: number
  issueId: string
  decisionRequestUUID: string
  decision: AiSuggestionDecisionKindT
  /** Lists the outcome refreshes, for a reader whose own scope lacks them. */
  projectStageId: string | null
  issueTypeId: string | null
  /** Queue identity of the PATCH from its mock 202; null until the enqueue has answered. */
  queueRequestUUID: string | null
  /** Null when the 202 was a `duplicate`: the row it matched carries an `enqueuedAt` we never saw. */
  enqueuedAt: number | null
  failure: AiSuggestionDecisionFailureT | null
}

export type AiSuggestionDecisionStartT = Pick<
  AiSuggestionDecisionMarkerT,
  'suggestionId' | 'issueId' | 'decisionRequestUUID' | 'decision' | 'projectStageId' | 'issueTypeId'
>

export type AiSuggestionQueueEntryT = {
  requestUUID: string
  enqueuedAt: number | null
  duplicate: boolean
}

type AiSuggestionDecisionStateT = {
  markers: Record<number, AiSuggestionDecisionMarkerT>
  /**
   * Suggestions whose decision the server has recorded. A decision is final, so none of them can
   * have one in flight again, while the queue row of the decision that landed may still be in the
   * last queue read when its outcome arrives.
   */
  decided: Record<number, true>
  /** Marks a decision as sent. A retry of the same decision keeps what the queue said before. */
  begin: (start: AiSuggestionDecisionStartT) => void
  /**
   * Puts back a marker derived from its queue row after a restart; a live marker wins, and a
   * suggestion already decided gets none.
   */
  restore: (marker: AiSuggestionDecisionMarkerT) => void
  attachQueueEntry: (
    suggestionId: number,
    decisionRequestUUID: string,
    entry: AiSuggestionQueueEntryT,
  ) => void
  fail: (
    suggestionId: number,
    decisionRequestUUID: string,
    failure: AiSuggestionDecisionFailureT,
  ) => void
  /** Fails every in-flight marker the queue row belongs to; see `queueEntryMatches`. */
  failQueueEntry: (
    requestUUID: string,
    enqueuedAt: number | null,
    failure: AiSuggestionDecisionFailureT,
  ) => void
  /** Drops the markers of suggestions whose decision the server has recorded, for good. */
  settle: (suggestionIds: readonly number[]) => void
  /** Drops one marker, e.g. after its refused row was removed; a new decision may follow. */
  drop: (suggestionId: number) => void
  reset: () => void
}

/**
 * Whether a queue row is this marker's PATCH. All writes of one issue share its requestUUID, so the
 * enqueue time tells two decisions on the same issue apart; a marker that only saw a `duplicate`
 * answer cannot, and matches by requestUUID alone.
 */
export function queueEntryMatches(
  marker: AiSuggestionDecisionMarkerT,
  requestUUID: string,
  enqueuedAt: number | null,
): boolean {
  if (marker.queueRequestUUID === null || marker.queueRequestUUID !== requestUUID) return false
  return marker.enqueuedAt === null || marker.enqueuedAt === enqueuedAt
}

export const useAiSuggestionDecisionStore = create<AiSuggestionDecisionStateT>(set => ({
  markers: {},
  decided: {},

  begin: start =>
    set(state => {
      const existing = state.markers[start.suggestionId]
      const retry = existing?.decisionRequestUUID === start.decisionRequestUUID
      return {
        markers: {
          ...state.markers,
          [start.suggestionId]: {
            ...start,
            queueRequestUUID: retry ? existing.queueRequestUUID : null,
            enqueuedAt: retry ? existing.enqueuedAt : null,
            failure: null,
          },
        },
      }
    }),

  restore: marker =>
    set(state =>
      state.markers[marker.suggestionId] || state.decided[marker.suggestionId]
        ? state
        : { markers: { ...state.markers, [marker.suggestionId]: marker } },
    ),

  attachQueueEntry: (suggestionId, decisionRequestUUID, entry) =>
    set(state => {
      const marker = state.markers[suggestionId]
      if (!marker || marker.decisionRequestUUID !== decisionRequestUUID) return state
      // A duplicate means an identical body is already queued: this decision's own earlier row.
      if (entry.duplicate && marker.queueRequestUUID !== null) return state
      return {
        markers: {
          ...state.markers,
          [suggestionId]: {
            ...marker,
            queueRequestUUID: entry.requestUUID,
            enqueuedAt: entry.duplicate ? null : entry.enqueuedAt,
          },
        },
      }
    }),

  fail: (suggestionId, decisionRequestUUID, failure) =>
    set(state => {
      const marker = state.markers[suggestionId]
      if (!marker || marker.decisionRequestUUID !== decisionRequestUUID) return state
      return { markers: { ...state.markers, [suggestionId]: { ...marker, failure } } }
    }),

  failQueueEntry: (requestUUID, enqueuedAt, failure) =>
    set(state => {
      let changed = false
      const markers = { ...state.markers }
      for (const marker of Object.values(state.markers)) {
        if (marker.failure !== null || !queueEntryMatches(marker, requestUUID, enqueuedAt)) continue
        markers[marker.suggestionId] = { ...marker, failure }
        changed = true
      }
      return changed ? { markers } : state
    }),

  settle: suggestionIds =>
    set(state => {
      const markers = { ...state.markers }
      const decided = { ...state.decided }
      let changed = false
      for (const id of suggestionIds) {
        if (id in markers) {
          delete markers[id]
          changed = true
        }
        if (!decided[id]) {
          decided[id] = true
          changed = true
        }
      }
      return changed ? { markers, decided } : state
    }),

  drop: suggestionId =>
    set(state => {
      if (!(suggestionId in state.markers)) return state
      const markers = { ...state.markers }
      delete markers[suggestionId]
      return { markers }
    }),

  reset: () => set({ markers: {}, decided: {} }),
}))
